import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  CheckCircle,
  ChevronDown,
  ClipboardList,
  LogOut,
  LucideAngularModule,
  Menu,
  User,
  X,
} from 'lucide-angular';
import { AuthService } from '../../../common/auth/auth.service';
import { UserProfile } from '../../../common/auth/auth.models';
import { ToastService } from '../../../common/services/toast.service';
import { AmplitudeService } from '../../../common/analytics/amplitude.service';
import { BehaviorSubject } from 'rxjs';

type StudentDashboardTab = 'assignments' | 'account';

@Component({
  selector: 'app-student-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    LucideAngularModule,
    RouterLink,
    RouterLinkActive,
    RouterOutlet,
  ],
  templateUrl: './student-dashboard.html',
  styleUrl: './student-dashboard.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StudentDashboardComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly toastService = inject(ToastService);
  private readonly amplitudeService = inject(AmplitudeService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly userProfile$ = new BehaviorSubject<UserProfile | null>(null);
  protected readonly isUserMenuOpen = signal(false);
  protected readonly isMobileSidebarOpen = signal(false);
  protected readonly toastState$ = this.toastService.state$;

  protected readonly icons = {
    User,
    ClipboardList,
    LogOut,
    CheckCircle,
    X,
    Menu,
    ChevronDown,
  };

  protected readonly tabs: { id: StudentDashboardTab; label: string; icon: typeof User; route: string }[] = [
    { id: 'assignments', label: 'Assignments', icon: ClipboardList, route: '/student/dashboard/assignments' },
    { id: 'account', label: 'Account', icon: User, route: '/student/dashboard/profile' },
  ];

  ngOnInit(): void {
    this.amplitudeService.trackEvent('student_dashboard_viewed');
    this.loadUserProfile();
  }

  private loadUserProfile(): void {
    this.authService
      .getProfile()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (profile) => {
          this.userProfile$.next(profile);
        },
        error: (error) => {
          console.error('Failed to load user profile:', error);
        },
      });
  }

  protected toggleMobileSidebar(): void {
    this.isMobileSidebarOpen.update((v) => !v);
  }

  protected closeMobileSidebar(): void {
    this.isMobileSidebarOpen.set(false);
  }

  protected toggleUserMenu(): void {
    this.isUserMenuOpen.update((v) => !v);
  }

  protected closeUserMenu(): void {
    this.isUserMenuOpen.set(false);
  }

  protected hideToast(): void {
    this.toastService.hide();
  }

  protected logout(): void {
    this.amplitudeService.trackEvent('logout');
    this.authService
      .logout()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.router.navigate(['/']);
      });
  }
}
